import { Controller } from "@hotwired/stimulus";
import { headers, jsonHeaders } from "../utils";
import ToastProgressBar from "../toast_progress_bar";

// Connects to data-controller="dropbox"
export default class extends Controller {
  static values = {
    url: String,
  };

  connect() {
    console.log("Dropbox controller connected!");
  }

  chooser(event) {
    event.preventDefault();

    const options = {
      success: (files) => {
        console.log("Files selected:", files);
        this.uploadFiles(files);
      },
      linkType: "direct",
      multiselect: true,
      extensions: [
        ".jpg",
        ".gif",
        ".png",
        ".pdf",
        ".doc",
        ".docx",
        ".ppt",
        ".heic",
        ".webp",
        ".pptx",
      ],
      folderselect: false,
      sizeLimit: 25 * 1024 * 1024, // 10 MB size limit
    };
    Dropbox.choose(options);
  }

  async uploadFiles(files) {
    // Create a toast progress bar
    const toast = new ToastProgressBar({
      message: `Processing ${files.length} files...`,
      backgroundColor: "success", // Use primary color for background
      progressColor: "dark", // Yellow progress bar for contrast
      offset: 16, // 16px offset from edges
      position: "bottom-start", // Bottom right corner
    });

    toast.show();

    let processed = 0;
    let errors = 0;
    let errorMessages = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      const body = {
        item: {
          name: file.name,
          media_url: file.link,
          bucket: "dropbox",
        },
      };

      this.createItem(body)
        .then((resp) => {
          // Update toast progress and message
          processed++;
          const progressPercent = ((processed + errors) / files.length) * 100;

          toast.updateProgress(progressPercent);
          toast.updateMessage(
            `Uploaded ${processed} of ${files.length} files (${errors} failed)...`
          );

          if (processed + errors === files.length) {
            // All files processed, update final toast message
            if (errors === 0) {
              toast.updateMessage(`Successfully uploaded ${processed} files`);
            } else {
              toast.updateMessage(
                `Uploaded ${processed} files (${errors} failed)`
              );
              // Display error messages
              errorMessages.forEach((msg) => window.notyf.error(msg));
            }
          }
        })
        .catch((error) => {
          errors++;
          errorMessages.push(error.message || "Upload failed");

          const progressPercent = ((processed + errors) / files.length) * 100;
          toast.updateProgress(progressPercent);
          toast.updateMessage(
            `Uploaded ${processed} of ${files.length} files (${errors} failed)...`
          );

          // Only show individual errors if there aren't too many
          if (errorMessages.length < 5) {
            window.notyf.error(error.message || "Upload failed");
          }

          console.log("error", error);
        });
    }
  }

  createItem(body) {
    return new Promise((resolve, reject) => {
      console.log("Creating item with body:", body);

      fetch(this.urlValue, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify(body),
      })
        .then((resp) => {
          console.log("Response status:", resp.status); // Add this to debug

          if (!resp.ok) {
            // For 400 errors, directly create a more specific error without trying to parse JSON
            if (resp.status === 400) {
              // Note: Use === instead of ==
              console.log("400 error detected for file:", body.item.name); // Debug log
              throw new Error(
                `${body.item.name} already exists in this project.`
              );
            }

            // For other errors, try to parse the JSON response
            return resp
              .json()
              .then((errorData) => {
                console.log("Error data:", errorData); // Debug log
                throw new Error(
                  `Error uploading file: ${body.item.name} - ${
                    errorData.error || "Unknown error"
                  }`
                );
              })
              .catch((jsonError) => {
                console.log("JSON parsing error:", jsonError); // Debug log
                throw new Error(`Error uploading file: ${body.item.name}`);
              });
          } else {
            return resp.text();
          }
        })
        .then((html) => {
          // Only try to render if we have HTML content
          if (html && html.trim()) {
            try {
              Turbo.renderStreamMessage(html);
            } catch (e) {
              console.warn("Error rendering Turbo stream:", e);
              // Continue despite rendering error
            }
          }
          resolve(true);
        })
        .catch((error) => {
          console.log("Final error in createItem:", error.message); // Add this to debug
          reject(error);
        });
    });
  }
}
